/**
 * Normalize an API confidence value to a rounded percentage (0–100).
 * The backend nominally sends a 0–1 float, but values can arrive as numeric
 * strings (JSON serialization) — coerce both. Returns null when missing or
 * unparseable so callers can hide the label entirely.
 */
export const confidencePercent = (value: unknown): number | null => {
  const n =
    typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  // Tolerate an already-percent scale, just in case.
  return Math.round(n <= 1 ? n * 100 : n);
};

/**
 * Minimal structural shape of a per-image classification label — matches the
 * backend's ImageClassificationLabel DTO fields we render.
 */
export interface ImageLabelLike {
  is_primary: boolean;
  is_active: boolean;
  classification?: string | null;
  confidence?: number | string | null;
}

/** Primary active label, falling back to any active label, then the first. */
export const primaryLabel = <T extends ImageLabelLike>(labels: T[] | null | undefined): T | null =>
  labels?.find((l) => l.is_primary && l.is_active) ?? labels?.find((l) => l.is_active) ?? labels?.[0] ?? null;

/** Confidence percent of an image's primary label, or null when absent. */
export const labelConfidencePercent = (labels: ImageLabelLike[] | null | undefined): number | null =>
  confidencePercent(primaryLabel(labels)?.confidence);
